/* BKR Vergelijk met 1ratio.nl — GGD-inspectieproof BKR-controle voor KDV.

   Directors/Locatiemanagers kunnen hier een groepje kinderen samenstellen (met
   leeftijd in maanden), en zien Webster's berekening naast de officiële SZW/
   Kinderopvangbesluit-conforme calculator. Ook een 1-klik testrunner voor
   6 standaard SZW-scenario's om te bewijzen dat de engine 100% correct is. */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Play, CheckCircle2, XCircle, AlertTriangle, PlayCircle, Baby } from "lucide-react";

const PRESET_LEEFTIJDEN = [
  { label: "Baby 0 mnd", months: 0 },
  { label: "6 mnd", months: 6 },
  { label: "1 jaar", months: 12 },
  { label: "18 mnd", months: 18 },
  { label: "2 jaar", months: 24 },
  { label: "30 mnd", months: 30 },
  { label: "3 jaar", months: 36 },
  { label: "3,5 jaar", months: 42 },
];

export default function BKRVergelijkPage() {
  const { user } = useAuth();
  const [kinderen, setKinderen] = useState([{ id: 1, months: 8 }, { id: 2, months: 18 }, { id: 3, months: 40 }]);
  const [nextId, setNextId] = useState(4);
  const [pmAanwezig, setPmAanwezig] = useState(1);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testcases, setTestcases] = useState([]);
  const [testResults, setTestResults] = useState({}); // {index: "pass"|"fail"|null}
  const [testRunning, setTestRunning] = useState(false);

  useEffect(() => {
    api.get("/bkr/vergelijk/testcases")
      .then(r => setTestcases(r.data.cases || []))
      .catch(() => {});
  }, []);

  const addKind = (months = 12) => {
    setKinderen(prev => [...prev, { id: nextId, months }]);
    setNextId(id => id + 1);
  };
  const removeKind = id => setKinderen(prev => prev.filter(k => k.id !== id));
  const setKindMonths = (id, m) => setKinderen(prev => prev.map(k => k.id === id ? { ...k, months: m } : k));

  const verticaalPreset = () => {
    setKinderen([
      { id: 1, months: 6 }, { id: 2, months: 9 },        // 2 baby
      { id: 3, months: 15 }, { id: 4, months: 18 }, { id: 5, months: 22 }, // 3 dreumes
      { id: 6, months: 27 }, { id: 7, months: 30 }, { id: 8, months: 33 }, // 3 twee
      { id: 9, months: 38 }, { id: 10, months: 44 },     // 2 drie
    ]);
    setNextId(11);
    toast.info("Verticale groep geladen (10 kinderen — vereist 3 PMs)");
  };

  const bereken = async () => {
    setBusy(true);
    try {
      const r = await api.post("/bkr/vergelijk", {
        kinderen: kinderen.map(k => ({ leeftijd_maanden: parseInt(k.months, 10) || 0 })),
        pm_aanwezig: pmAanwezig,
      });
      setResult(r.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const runAllTests = async () => {
    setTestRunning(true);
    setTestResults({});
    const results = {};
    for (let i = 0; i < testcases.length; i++) {
      try {
        const r = await api.post("/bkr/vergelijk", {
          kinderen: testcases[i].kinderen,
          pm_aanwezig: null,
        });
        const officieel = r.data.officieel_1ratio_nl?.minimum_pms;
        const webster = r.data.webster_engine?.minimum_pms;
        const identiek = r.data.identiek;
        const verwacht = testcases[i].verwacht_min_pms;
        results[i] = (officieel === verwacht && webster === verwacht && identiek) ? "pass" : "fail";
        setTestResults({ ...results });
      } catch { results[i] = "fail"; setTestResults({ ...results }); }
    }
    setTestRunning(false);
    const passed = Object.values(results).filter(v => v === "pass").length;
    if (passed === testcases.length) toast.success(`Alle ${passed}/${testcases.length} SZW-testcases geslaagd`);
    else toast.error(`${passed}/${testcases.length} testcases geslaagd — check details`);
  };

  const leeftijdVerdeling = useMemo(() => {
    const counts = { "0jr": 0, "1jr": 0, "2jr": 0, "3jr": 0, "buiten": 0 };
    for (const k of kinderen) {
      const m = parseInt(k.months, 10) || 0;
      if (m < 12) counts["0jr"] += 1;
      else if (m < 24) counts["1jr"] += 1;
      else if (m < 36) counts["2jr"] += 1;
      else if (m < 48) counts["3jr"] += 1;
      else counts["buiten"] += 1;
    }
    return counts;
  }, [kinderen]);

  return (
    <div className="min-h-screen bg-neutral-50 p-4 md:p-6" data-testid="bkr-vergelijk-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-4">
          <h1 className="text-2xl md:text-3xl font-display text-neutral-900">BKR Vergelijk met 1ratio.nl</h1>
          <p className="text-sm text-neutral-600 mt-1">
            Officieel SZW/Kinderopvangbesluit-conforme controle. Alleen voor <strong>KDV</strong> (0-4 jaar).
            De officiële calculator gebruikt exacte breuken (Fraction) — geen floating-point-fouten.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* LINKS: input */}
          <Card className="p-5 bg-white" data-testid="bkr-input-card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-neutral-900">Kinderen in groep ({kinderen.length})</div>
              <Button size="sm" variant="outline" onClick={verticaalPreset} data-testid="bkr-preset-verticaal">
                <Baby className="h-3.5 w-3.5 mr-1" /> Verticaal (10)
              </Button>
            </div>

            <div className="grid grid-cols-5 gap-1 mb-3 text-xs" data-testid="bkr-leeftijd-verdeling">
              <div className="rounded bg-pink-50 border border-pink-200 p-2 text-center"><div className="text-pink-700 font-semibold">{leeftijdVerdeling["0jr"]}</div><div className="text-pink-600">0j</div></div>
              <div className="rounded bg-amber-50 border border-amber-200 p-2 text-center"><div className="text-amber-700 font-semibold">{leeftijdVerdeling["1jr"]}</div><div className="text-amber-600">1j</div></div>
              <div className="rounded bg-emerald-50 border border-emerald-200 p-2 text-center"><div className="text-emerald-700 font-semibold">{leeftijdVerdeling["2jr"]}</div><div className="text-emerald-600">2j</div></div>
              <div className="rounded bg-blue-50 border border-blue-200 p-2 text-center"><div className="text-blue-700 font-semibold">{leeftijdVerdeling["3jr"]}</div><div className="text-blue-600">3j</div></div>
              <div className="rounded bg-neutral-50 border border-neutral-200 p-2 text-center"><div className="text-neutral-700 font-semibold">{leeftijdVerdeling["buiten"]}</div><div className="text-neutral-500">4+</div></div>
            </div>

            <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
              {kinderen.map((k, idx) => (
                <div key={k.id} className="flex items-center gap-2" data-testid={`bkr-kind-${k.id}`}>
                  <span className="text-xs text-neutral-400 w-6">#{idx + 1}</span>
                  <Input
                    type="number"
                    min={0}
                    max={72}
                    value={k.months}
                    onChange={e => setKindMonths(k.id, e.target.value)}
                    className="h-9 max-w-[100px]"
                    data-testid={`bkr-kind-months-${k.id}`}
                  />
                  <span className="text-xs text-neutral-500">mnd</span>
                  <div className="text-xs text-neutral-400 flex-1">
                    {k.months < 12 ? "baby" : k.months < 24 ? "dreumes" : k.months < 36 ? "peuter 2j" : k.months < 48 ? "peuter 3j" : "4+ (buiten KDV)"}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeKind(k.id)} data-testid={`bkr-kind-remove-${k.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {PRESET_LEEFTIJDEN.map(p => (
                <Button key={p.months} size="sm" variant="outline" onClick={() => addKind(p.months)} className="h-8 text-xs" data-testid={`bkr-add-${p.months}`}>
                  <Plus className="h-3 w-3 mr-1" /> {p.label}
                </Button>
              ))}
            </div>

            <div className="mt-4 pt-3 border-t border-neutral-200">
              <Label htmlFor="pm-aanwezig" className="text-xs">Aanwezige PM's (voor naleving-check)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  id="pm-aanwezig"
                  type="number"
                  min={0}
                  max={20}
                  value={pmAanwezig}
                  onChange={e => setPmAanwezig(parseInt(e.target.value, 10) || 0)}
                  className="h-10 max-w-[100px]"
                  data-testid="bkr-pm-aanwezig"
                />
                <Button onClick={bereken} disabled={busy || kinderen.length === 0} className="text-white flex-1 h-10" style={{ background: "var(--sage)" }} data-testid="bkr-bereken">
                  <Play className="h-4 w-4 mr-1.5" /> {busy ? "Berekenen…" : "Bereken en vergelijk"}
                </Button>
              </div>
            </div>
          </Card>

          {/* RECHTS: result */}
          <Card className="p-5 bg-white" data-testid="bkr-result-card">
            {!result && (
              <div className="text-center text-neutral-400 py-16">
                <Play className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <div className="text-sm">Voeg kinderen toe en klik "Bereken en vergelijk"</div>
              </div>
            )}

            {result && (
              <>
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={result.identiek ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-red-100 text-red-800 border-red-200"} data-testid="bkr-identiek-badge">
                      {result.identiek ? <><CheckCircle2 className="h-3 w-3 mr-1 inline" />Berekeningen identiek</> : <><AlertTriangle className="h-3 w-3 mr-1 inline" />Verschil gedetecteerd</>}
                    </Badge>
                  </div>
                </div>

                {/* Side-by-side */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3" data-testid="bkr-officieel-col">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-700 mb-1">Officieel · 1ratio.nl</div>
                    <div className="text-3xl font-display text-emerald-900">{result.officieel_1ratio_nl.minimum_pms}</div>
                    <div className="text-xs text-emerald-700 mt-0.5">PMs vereist</div>
                    <div className="text-xs text-neutral-600 mt-2">Som: <span className="font-mono">{result.officieel_1ratio_nl.som_bijdragen_exact}</span></div>
                    <div className="text-[10px] text-neutral-500 font-mono">({result.officieel_1ratio_nl.som_bijdragen_decimal})</div>
                  </div>
                  <div className="rounded-lg border-2 border-blue-300 bg-blue-50 p-3" data-testid="bkr-webster-col">
                    <div className="text-[10px] uppercase tracking-widest text-blue-700 mb-1">Webster engine</div>
                    <div className="text-3xl font-display text-blue-900">{result.webster_engine.minimum_pms}</div>
                    <div className="text-xs text-blue-700 mt-0.5">PMs vereist</div>
                    <div className="text-xs text-neutral-600 mt-2">Som: <span className="font-mono">{result.webster_engine.som_bijdragen_decimal}</span></div>
                  </div>
                </div>

                {result.delta && (result.delta.minimum_pms_delta !== 0 || Math.abs(result.delta.som_bijdragen_delta_decimal) > 1e-9) && (
                  <div className="rounded bg-red-50 border border-red-200 p-2 text-xs text-red-800 mb-3" data-testid="bkr-delta-warning">
                    <XCircle className="h-3 w-3 inline mr-1" /> Δ minimum: {result.delta.minimum_pms_delta} · Δ som: {result.delta.som_bijdragen_delta_decimal}
                  </div>
                )}

                {/* Naleving */}
                {result.naleving && (
                  <div className={`rounded-lg p-3 border-2 ${result.naleving.voldoet ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300"}`} data-testid="bkr-naleving">
                    <div className={`text-sm font-semibold ${result.naleving.voldoet ? "text-emerald-900" : "text-red-900"}`}>
                      {result.naleving.voldoet ? <><CheckCircle2 className="h-4 w-4 inline mr-1" /> BKR voldoet</> : <><XCircle className="h-4 w-4 inline mr-1" /> BKR overschrijding</>}
                    </div>
                    <div className="text-xs text-neutral-700 mt-1">
                      Aanwezig: <strong>{result.naleving.pm_aanwezig}</strong> · Vereist: <strong>{result.naleving.pm_vereist_officieel}</strong>
                      {result.naleving.tekort > 0 && <> · <span className="text-red-700 font-semibold">Tekort: {result.naleving.tekort}</span></>}
                      {result.naleving.over_bezet > 0 && <> · <span className="text-emerald-700">Over: {result.naleving.over_bezet}</span></>}
                    </div>
                  </div>
                )}

                {/* Leeftijdsverdeling */}
                <div className="mt-4 text-xs text-neutral-600">
                  <div className="uppercase tracking-widest text-[10px] text-neutral-500 mb-1">Officiële verdeling</div>
                  <div className="flex gap-2 flex-wrap">
                    {Object.entries(result.officieel_1ratio_nl.ratio_leeftijdsverdeling).map(([k, v]) => v > 0 && (
                      <span key={k} className="px-2 py-0.5 bg-neutral-100 rounded font-mono">{k}: {v}</span>
                    ))}
                    {result.officieel_1ratio_nl.kinderen_buiten_kdv > 0 && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded">buiten KDV: {result.officieel_1ratio_nl.kinderen_buiten_kdv}</span>
                    )}
                  </div>
                </div>
              </>
            )}
          </Card>
        </div>

        {/* Testcase runner */}
        <Card className="p-5 bg-white mt-4" data-testid="bkr-testcases-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-semibold text-neutral-900">SZW-conformiteit testrunner</div>
              <div className="text-xs text-neutral-500">Draai alle standaard scenario's om te bewijzen dat Webster 100% Kinderopvangbesluit-conform is.</div>
            </div>
            <Button onClick={runAllTests} disabled={testRunning || testcases.length === 0} className="text-white" style={{ background: "var(--sage)" }} data-testid="bkr-run-all-tests">
              <PlayCircle className="h-4 w-4 mr-1.5" /> {testRunning ? "Draaien…" : `Draai alle ${testcases.length}`}
            </Button>
          </div>

          <div className="space-y-1">
            {testcases.map((tc, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-neutral-50" data-testid={`bkr-testcase-${i}`}>
                <div className="w-6">
                  {testResults[i] === "pass" && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                  {testResults[i] === "fail" && <XCircle className="h-4 w-4 text-red-600" />}
                  {!testResults[i] && <div className="h-4 w-4 rounded-full border border-neutral-300" />}
                </div>
                <div className="flex-1 text-sm text-neutral-800">{tc.naam}</div>
                <div className="text-xs text-neutral-500 font-mono">→ {tc.verwacht_min_pms} PM</div>
                <div className="text-[11px] text-neutral-400 hidden md:block max-w-xs truncate">{tc.toelichting}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
